import React, { useState } from 'react';
import { signIn, signUp, resetPassword, useAuth, friendlyAuthError } from '../AuthContext';
import SITE from '../site';

export default function Login() {
  const { denied } = useAuth();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const switchMode = (next) => {
    setMode(next);
    setError(null);
    setInfo(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      if (mode === 'reset') {
        await resetPassword(email.trim());
        setInfo(`Password reset email sent to ${email.trim()}. Check your inbox (and spam), then sign in with the new password.`);
        setMode('signin');
      } else if (mode === 'signup') {
        await signUp(email.trim(), password);
      } else {
        await signIn(email.trim(), password);
      }
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  };

  const title =
    mode === 'signup' ? 'Create account' : mode === 'reset' ? 'Reset password' : 'Sign in';

  return (
    <div className="cs-modal" style={{ maxWidth: 400 }}>
      <div className="cs-brand" style={{ fontSize: 28, marginBottom: 6 }}>
        {SITE.brandA}<span>{SITE.brandB}</span>
      </div>
      <h2 style={{ fontSize: 22, marginTop: 0 }}>{title}</h2>

      {(error || denied) && (
        <div className="cs-empty" style={{ padding: 12, marginBottom: 14, color: 'var(--accent)' }}>
          {error || denied}
        </div>
      )}
      {info && (
        <div className="cs-empty" style={{ padding: 12, marginBottom: 14 }}>
          {info}
        </div>
      )}

      <form onSubmit={submit}>
        <div className="cs-field">
          <label>Email</label>
          <input
            type="email"
            className="cs-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </div>
        {mode !== 'reset' && (
          <div className="cs-field">
            <label>Password</label>
            <input
              type="password"
              className="cs-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
          </div>
        )}
        {mode === 'reset' && (
          <p className="cs-help" style={{ marginTop: 0, marginBottom: 14 }}>
            We’ll email a link to set a new password for your {SITE.siteName} account.
          </p>
        )}
        <button type="submit" className="cs-btn cs-btn-primary" style={{ width: '100%' }} disabled={busy}>
          {busy
            ? 'Please wait…'
            : mode === 'signup'
              ? 'Create account'
              : mode === 'reset'
                ? 'Send reset email'
                : 'Sign in'}
        </button>
      </form>

      <div style={{ textAlign: 'center', marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {mode === 'signin' && (
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => switchMode('reset')}>
            Forgot password?
          </button>
        )}
        {mode === 'reset' ? (
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => switchMode('signin')}>
            Back to sign in
          </button>
        ) : (
          <button
            type="button"
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={() => switchMode(mode === 'signup' ? 'signin' : 'signup')}
          >
            {mode === 'signup'
              ? 'Already have an account? Sign in'
              : 'Need an account? Create one'}
          </button>
        )}
      </div>
      {mode === 'signin' && (
        <p className="cs-help" style={{ marginTop: 14, textAlign: 'center' }}>
          First time on {SITE.siteName}? Accounts don’t carry over from other sites —
          use Create one with this email.
        </p>
      )}
    </div>
  );
}
